// NOTE: update as needed. There are more detailed types in swagger model
// OTHER NOTE: We don't need to do this, we can just infer types from our zod schemas, so that we don't have to write both.

import type { ReactElement } from 'react';
import { z } from 'zod';

const countryBaseSchema = z.object({
  id: z.string(),
  cm: z.string(),
  n: z.string(),
  s: z.boolean(),
  p: z.array(z.unknown()) // this currently shows up as [] in responses from DP
});

export const playgroundPlaceSchema = z.object({
  id: z.number(), // dp id (also called 'placeId' in DP models)
  cm: z.string().nullable(), // cms id // NOTE: this is a stringified number in the swagger model: e.g. "143826232"
  n: z.string(), // place name
  s: z.boolean(), // selected
  geo: z.object({
    latitude: z.number(),
    longitude: z.number()
  }),
  c: countryBaseSchema,
  ai: z.string().nullable().optional() // AI suggestions for the place
});

export type PlaygroundPlace = z.infer<typeof playgroundPlaceSchema>;

export const playgroundCountrySchema = z.object({
  // similar to playground place
  id: z.string(),
  cm: z.string(),
  n: z.string(),
  s: z.boolean(),
  p: playgroundPlaceSchema.array()
});
export type PlaygroundCountry = z.infer<typeof playgroundCountrySchema>;

export const playgroundCountryGroupSchema = z.object({
  id: z.string(),
  n: z.string(),
  r: z.number(),
  s: z.boolean(),
  c: playgroundCountrySchema.array()
});
export type PlaygroundCountryGroup = z.infer<typeof playgroundCountryGroupSchema>;

export const chosenDurationSchema = z.union([
  z.number(),
  z.enum(['WEEKEND', 'LONG_WEEKEND', 'WEEK', 'FORTNIGHT', 'CUSTOM']).nullable()
]);

export const playgroundContextSchema = z.object({
  availableActivityAttributes: z.null(),
  chosenDepartureDate: z.string().nullable(),
  chosenDepartureDateType: z.enum(['EXACT', 'FLEXIBLE']).nullable(),
  chosenDuration: chosenDurationSchema,
  chosenOrigin: playgroundPlaceSchema,
  chosenDestination: playgroundPlaceSchema.optional().nullable(),
  chosenPreferences: z.null(),
  chosenReturnDate: z.union([z.null(), z.string()]),
  chosenRouteFiltering: z.null().optional(),
  pinnedActivities: z.null().optional(),
  pinnedHighlightedRoutes: z.null(),
  pinnedPlaces: z.null().optional(),
  playgroundContextId: z.string(),
  selectedDestinations: z.object({
    countries: playgroundCountrySchema.array(),
    places: playgroundPlaceSchema.array()
  }),
  highlightedRoutes: z.null(),
  selectableDestinations: playgroundCountryGroupSchema.array(),
  minimumDuration: z.number().nullable().optional(),
  recommendedDestinations: z.null().optional(),
  displayActivities: z.null().optional(),
  responseMessages: z.array(z.string()).optional(),
  isDefaultContext: z.boolean().optional()
});

const attributeSchema = z.object({
  id: z.number(),
  description: z.string(),
  weight: z.number()
});

const countrySchema = z.object({
  countryId: z.string(),
  countryName: z.string(),
  cmsId: z.string().nullable()
});

const geoCoordinatesSchema = z.object({
  latitude: z.number(),
  longitude: z.number()
});

const skeletonPlaceSchema = z.object({
  id: z.number(),
  cmsId: z.string().nullable(),
  regionIds: z.array(z.number()),
  locationName: z.string(),
  country: countrySchema,
  geoCoordinates: geoCoordinatesSchema,
  description: z.string(),
  extraNotes: z.string(),
  image: z.string(),
  imageCredit: z.string().nullable(),
  seasonalContentCmsId: z.string().nullable(),
  attributes: z.array(attributeSchema),
  timezoneID: z.string().nullable(),
  hasSufficientAccommodation: z.boolean(),
  userSpecificDescription: z.string().nullable()
});

const skeletonLegSchema = z.object({
  '@type': z.literal('Leg'),
  itemStartDate: z.string(), // 'yyyy-mm-dd'
  itemEndDate: z.string(), // 'yyyy-mm-dd'
  itemId: z.number(),
  id: z.string(),
  estimatedMinutes: z.number().nullable(),
  estimatedDistance: z.number().nullable(),
  title: z.string().nullable(),
  fromPlace: skeletonPlaceSchema,
  toPlace: skeletonPlaceSchema,
  numberOfNights: z.number(),
  startPlace: skeletonPlaceSchema,
  endPlace: skeletonPlaceSchema
});

const skeletonStaySchema = z.object({
  '@type': z.literal('Stay'),
  itemStartDate: z.string(), // 'yyyy-mm-dd'
  itemEndDate: z.string(), // 'yyyy-mm-dd'
  itemId: z.number(),
  place: skeletonPlaceSchema,
  durationNights: z.number(),
  startPlace: skeletonPlaceSchema,
  endPlace: skeletonPlaceSchema
});

export const skeletonJourneySchema = z.object({
  journeyItems: z.array(z.union([skeletonLegSchema, skeletonStaySchema])),
  uniqueId: z.string(),
  shortId: z.string().nullable(),
  creationDate: z.string().nullable(),
  journeyTitle: z.string()
});

export type JourneySkeleton = z.infer<typeof skeletonJourneySchema>;
export type SkeletonLeg = z.infer<typeof skeletonLegSchema>;
export type SkeletonStay = z.infer<typeof skeletonStaySchema>;
export type SkeletonPlace = z.infer<typeof skeletonPlaceSchema>;

export const generateSkeletonJourneysRequestSchema = z.object({
  playgroundInputContext: playgroundContextSchema,
  internalUser: z.string().optional(),
  travellerMixes: z.array(
    z.object({
      adultCount: z.number(),
      childAges: z.array(z.number()),
      numberOfRooms: z.number()
    })
  ),
  preferredTransportationClass: z.enum(['STANDARD', 'FIRST_CLASS']),
  twinRoomPreferred: z.boolean()
});

export const generateJourneysDefaults = {
  playgroundInputContext: {},
  preferredTransportationClass: 'STANDARD' as const,
  travellerMixes: [
    {
      adultCount: 2,
      childAges: [],
      numberOfRooms: 1
    }
  ],
  twinRoomPreferred: false
};

export const generateJourneyFromSkeletonRequestSchema = z.object({
  playgroundInputContext: playgroundContextSchema,
  internalUser: z.string().optional(),
  travellerMixes: z.array(
    z.object({
      adultCount: z.number(),
      childAges: z.array(z.number()),
      numberOfRooms: z.number()
    })
  ),
  countryCodeOfResidence: z.string(),
  journeyId: z.string()
});

export type GenerateJourneyFromSkeletonRequest = z.infer<typeof generateJourneyFromSkeletonRequestSchema>;

export const generateJourneysFromSkeletonDefaults = {
  playgroundInputContext: {},
  preferredTransportationClass: 'STANDARD' as const,
  travellerMixes: [
    {
      adultCount: 2,
      childAges: [],
      numberOfRooms: 1
    }
  ],
  twinRoomPreferred: false
};

export type PlaygroundContext = z.infer<typeof playgroundContextSchema>;

export type PlaygroundDestinationSection = {
  name: string;
  items: PlaygroundPlace[];
  altHeading?: ReactElement;
};
